/**
 * <s-shirt> — one floating shirt on the homepage canvas.
 * Opens its product dialog on click. Mouse and keyboard open it straight away
 * (desktop hover styles live behind pointer-fine, so touch never matches them).
 * Touch/pen: one tap adds .is-tapped (dark tint + name and price), then opens
 * the dialog ~270ms later; the tint clears on open, close, and scroll.
 * On open, focus goes to the card container (tabindex="-1"), never the dots.
 * Keeps price/availability in sync with the size radios
 * (named "id", the variant source of truth), and adds to the bag through
 * Shopify Standard Actions so the header badge and bag drawer refresh.
 * After adding, the button shows a check and "In bag (n)" for that variant.
 */
const TAP_OPEN_DELAY = 270;
const SWIPE_CLOSE_DISTANCE = 110;
const SWIPE_CLOSE_VELOCITY = 0.5; // px per ms

/*
 * Mobile (under 768px) only:
 * - the dialog is a bottom sheet; the page behind is locked while it is open
 * - swiping the sheet down (from the top of its scroll) closes it
 * - closing never returns focus to the shirt (no ring, no tint); keyboard opens still do
 * - Add to bag bumps the header badge instantly, before the cart partial refresh lands
 */
const MOBILE_QUERY = '(max-width: 47.999rem)';

class SShirt extends HTMLElement {
  #ac;
  #addAc;
  #counts = new Map();
  #openTimer = 0;
  #returnFocusToPage = false;
  #lockedScrollY = null;
  #mobile = window.matchMedia(MOBILE_QUERY);

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const trigger = this.querySelector('[data-shirt-trigger]');
    const dialog = this.#dialog;
    const touchScreen = window.matchMedia('(hover: none), (pointer: coarse)');

    // One handler per shirt. Keyboard activation (detail 0) and mouse open at once;
    // touch/pen show the tint first, then open.
    trigger?.addEventListener('click', (event) => {
      if (event.detail === 0) {
        this.#open('keyboard');
        return;
      }
      const type = event.pointerType;
      const isTouch = type ? type !== 'mouse' : touchScreen.matches;
      if (isTouch) this.#tapOpen();
      else this.#open('mouse');
    }, opts);

    dialog?.addEventListener('close', () => {
      this.#clearTap();
      this.#unlockPage();
      dialog.removeAttribute('data-dragging');
      dialog.style.removeProperty('transform');
      if (this.#returnFocusToPage) {
        this.#returnFocusToPage = false;
        this.#releaseFocus();
        // Run again after any other close handler (and Safari's own focus restore) has finished.
        requestAnimationFrame(() => this.#releaseFocus());
        setTimeout(() => this.#releaseFocus(), 320);
      }
    }, opts);

    if (dialog) this.#setupSwipe(dialog);

    window.addEventListener('scroll', () => {
      if (this.classList.contains('is-tapped')) this.classList.remove('is-tapped');
    }, { signal: this.#ac.signal, passive: true });

    const form = this.#form;
    if (form) {
      form.addEventListener('change', (event) => {
        if (event.target.matches?.('input[name="id"]')) this.#syncVariant();
      }, opts);

      form.addEventListener('submit', (event) => {
        if (!window.Shopify?.actions?.updateCart) return;
        event.preventDefault();
        this.#add();
      }, opts);
    }

    this.#setupXR();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#addAc?.abort();
    clearTimeout(this.#openTimer);
    this.#openTimer = 0;
  }

  get #dialog() {
    const id = this.dataset.dialog;
    return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
  }

  get #form() {
    return this.querySelector('form[data-shirt-form]');
  }

  get #selectedInput() {
    const form = this.#form;
    if (!form) return null;
    return form.querySelector('input[name="id"]:checked') || form.querySelector('input[name="id"][type="hidden"]');
  }

  #tapOpen() {
    if (this.#openTimer || this.#dialog?.open) return;
    for (const other of document.querySelectorAll('s-shirt.is-tapped')) {
      if (other !== this) other.classList.remove('is-tapped');
    }
    this.classList.add('is-tapped');
    this.#openTimer = setTimeout(() => {
      this.#openTimer = 0;
      this.#open('touch');
    }, TAP_OPEN_DELAY);
  }

  /** Blur anything focused inside this shirt and drop any text selection, so nothing draws a line or ring. */
  #releaseFocus() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body && this.contains(active)) active.blur();
    const selection = window.getSelection?.();
    if (selection && !selection.isCollapsed) selection.removeAllRanges();
  }

  /** Mobile: pin the page in place behind the sheet (iOS ignores overflow:hidden for touch scrolling). */
  #lockPage() {
    if (!this.#mobile.matches || this.#lockedScrollY !== null) return;
    this.#lockedScrollY = window.scrollY;
    const body = document.body.style;
    body.position = 'fixed';
    body.top = `-${this.#lockedScrollY}px`;
    body.left = '0';
    body.right = '0';
    body.width = '100%';
  }

  #unlockPage() {
    if (this.#lockedScrollY === null) return;
    const y = this.#lockedScrollY;
    this.#lockedScrollY = null;
    const body = document.body.style;
    for (const prop of ['position', 'top', 'left', 'right', 'width']) body.removeProperty(prop);
    window.scrollTo({ top: y, left: 0, behavior: 'instant' });
  }

  /** Mobile: drag the sheet down from the top of its scroll to close it. */
  #setupSwipe(dialog) {
    const card = dialog.querySelector('[data-shirt-card]');
    if (!card) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let distance = 0;
    let tracking = false;
    let dragging = false;
    const opts = { signal: this.#ac.signal };

    card.addEventListener('touchstart', (event) => {
      if (!this.#mobile.matches || event.touches.length !== 1) return;
      tracking = card.scrollTop <= 0;
      dragging = false;
      distance = 0;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      startTime = performance.now();
    }, { ...opts, passive: true });

    card.addEventListener('touchmove', (event) => {
      if (!tracking) return;
      const dx = event.touches[0].clientX - startX;
      const dy = event.touches[0].clientY - startY;
      if (!dragging) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        // Upward or sideways (gallery swipe) gestures belong to the browser.
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy) || card.scrollTop > 0) {
          tracking = false;
          return;
        }
        dragging = true;
        dialog.setAttribute('data-dragging', '');
      }
      event.preventDefault();
      distance = Math.max(0, dy);
      dialog.style.transform = `translateY(${distance}px)`;
    }, { ...opts, passive: false });

    const end = () => {
      tracking = false;
      if (!dragging) return;
      dragging = false;
      const velocity = distance / Math.max(1, performance.now() - startTime);
      dialog.removeAttribute('data-dragging');
      // Clearing the inline transform lets the sheet animate from where the finger left it.
      dialog.style.removeProperty('transform');
      if (distance > SWIPE_CLOSE_DISTANCE || velocity > SWIPE_CLOSE_VELOCITY) dialog.close();
    };
    card.addEventListener('touchend', end, opts);
    card.addEventListener('touchcancel', end, opts);
  }

  /** Mobile: show the new bag count right away; the cart-count partial swap confirms it. */
  #bumpBadge(delta) {
    if (!this.#mobile.matches) return;
    const holder = document.querySelector('#header-cart-trigger span.relative');
    if (!holder) return;
    let badge = holder.querySelector('span.absolute');
    const next = Math.max(0, (parseInt(badge?.textContent || '0', 10) || 0) + delta);
    if (next === 0) {
      badge?.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'absolute -top-1.5 -end-2 flex items-center justify-center min-w-5 h-5 px-1 text-[11px] font-semibold leading-none bg-interactive text-interactive-text rounded-full ring-2 ring-surface';
      holder.append(badge);
    }
    badge.textContent = String(next);
  }

  #clearTap() {
    clearTimeout(this.#openTimer);
    this.#openTimer = 0;
    this.classList.remove('is-tapped');
  }

  #open(source) {
    const dialog = this.#dialog;
    if (!dialog || dialog.open) {
      this.#clearTap();
      return;
    }
    // Touch anywhere, and any non-keyboard open on mobile, hands focus back to the page on close.
    this.#returnFocusToPage = source === 'touch' || (source !== 'keyboard' && this.#mobile.matches);
    if (this.#returnFocusToPage) {
      // Nothing stays focused, so neither the dialog nor s-dialog has a shirt to refocus on close.
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
    }
    this.#lockPage();
    dialog.showModal();
    // showModal focuses the first focusable child (a dot); move focus to the card itself.
    dialog.querySelector('[data-shirt-card]')?.focus({ preventScroll: true });
    this.#clearTap();
    this.#refreshCounts();
  }

  #syncVariant() {
    const input = this.#selectedInput;
    if (!input) return;

    const price = this.querySelector('[data-shirt-price]');
    if (price && input.dataset.price) price.textContent = input.dataset.price;

    const available = input.dataset.available === 'true';
    const availability = this.querySelector('[data-shirt-availability]');
    if (availability) {
      availability.textContent = available ? availability.dataset.inStock : availability.dataset.soldOut;
    }

    const add = this.querySelector('[data-shirt-add]');
    if (add) {
      if (available) add.removeAttribute('aria-disabled');
      else add.setAttribute('aria-disabled', 'true');
    }

    this.#showError(false);
    this.#renderAddState();
  }

  async #refreshCounts() {
    const form = this.#form;
    const cartUrl = form?.dataset.cartUrl;
    if (!cartUrl) return;

    try {
      // Cart route comes from Liquid (routes.cart_url); .js returns the cart JSON.
      const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.#counts.clear();
      for (const item of cart.items || []) {
        const key = String(item.variant_id);
        this.#counts.set(key, (this.#counts.get(key) || 0) + item.quantity);
      }
      this.#renderAddState();
    } catch {
      // Counts are a nicety; the add flow still works without them.
    }
  }

  #renderAddState() {
    const input = this.#selectedInput;
    const add = this.querySelector('[data-shirt-add]');
    if (!input || !add) return;

    const count = this.#counts.get(String(input.value)) || 0;
    const label = add.querySelector('[data-add-label]');
    const bagIcon = add.querySelector('[data-icon-bag]');
    const checkIcon = add.querySelector('[data-icon-check]');
    const template = this.#form?.dataset.inBag || '';

    if (count > 0 && template) {
      label.textContent = template.replace('__n__', String(count));
      bagIcon?.classList.replace('inline-flex', 'hidden');
      checkIcon?.classList.replace('hidden', 'inline-flex');
    } else {
      label.textContent = label.dataset.default;
      checkIcon?.classList.replace('inline-flex', 'hidden');
      bagIcon?.classList.replace('hidden', 'inline-flex');
    }
  }

  async #add() {
    const input = this.#selectedInput;
    const add = this.querySelector('[data-shirt-add]');
    if (!input || !add || add.getAttribute('aria-disabled') === 'true') return;
    if (this.getAttribute('aria-busy') === 'true') return;

    this.#addAc?.abort();
    this.#addAc = new AbortController();
    const { signal } = this.#addAc;

    // Optimistic: show "In bag" the instant the button is pressed, then confirm with the cart.
    // The network round trip runs behind it; a failure rolls the count back and shows the error.
    const key = String(input.value);
    const previous = this.#counts.get(key) || 0;
    this.#counts.set(key, previous + 1);
    this.#renderAddState();
    this.#bumpBadge(1);

    this.setAttribute('aria-busy', 'true');
    this.#showError(false);

    const rollback = (message) => {
      this.#counts.set(key, Math.max(0, (this.#counts.get(key) || 1) - 1));
      this.#bumpBadge(-1);
      this.#renderAddState();
      this.#showError(true, message);
    };

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: key, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-shirt' } }, signal }
      );
      if (signal.aborted) return;

      if (result?.userErrors?.length) {
        rollback(result.userErrors[0].message);
      } else {
        this.#refreshCounts();
      }
    } catch {
      if (!signal.aborted) rollback();
    } finally {
      if (!signal.aborted) this.removeAttribute('aria-busy');
    }
  }

  #showError(show, message) {
    const error = this.querySelector('[data-shirt-error]');
    if (!error) return;
    if (show && message) error.textContent = message;
    error.hidden = !show;
  }

  #setupXR() {
    const models = this.querySelector('script[data-shirt-models]');
    if (!models || !window.Shopify?.loadFeatures) return;

    window.Shopify.loadFeatures([{
      name: 'shopify-xr',
      version: '1.0',
      onLoad: (error) => {
        if (error || !window.ShopifyXR) return;
        try {
          window.ShopifyXR.addModels(JSON.parse(models.textContent));
          window.ShopifyXR.setupXRElements();
        } catch {
          // AR stays unavailable on devices that can't load the model.
        }
      },
    }]);
  }
}

if (!customElements.get('s-shirt')) {
  customElements.define('s-shirt', SShirt);
}
